// src/components/AdminSideComponents/packages/PackageFormModal.jsx
import React, { useState, useEffect } from "react";
import {
  X,
  ImagePlus,
  Loader2,
  Plus,
  Trash2,
  Pencil,
} from "lucide-react";
import uploadFileToCloudinary from "../../../utils/uploadFileToCloudinary";
import axios from "axios";
import { showError, showSuccess } from "../../../utils/toastUtils";

const PackageFormModal = ({ isOpen, onClose, packageData = null, onSuccess }) => {
  const isEditMode = !!packageData;

  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    price: "",
    discountedPrice: "",
    banner: null,
    isActive: true,
    services: [], // array of strings - manually entered
  });

  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  // For the service input field
  const [serviceInput, setServiceInput] = useState("");

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && packageData) {
        setFormData({
          name: packageData.name || "",
          category: packageData.category || "",
          description: packageData.description || "",
          price: packageData.price || "",
          discountedPrice: packageData.discountedPrice || "",
          banner: null, // new file only
          isActive: packageData.isActive ?? true,
          services: packageData.services || [],
        });
        setPreview(packageData.banner || null);
      } else {
        // Reset for create mode
        setFormData({
          name: "",
          category: "",
          description: "",
          price: "",
          discountedPrice: "",
          banner: null,
          isActive: true,
          services: [],
        });
        setPreview(null);
      }
      setServiceInput("");
    }
  }, [isOpen, packageData, isEditMode]);

  const handleAddService = () => {
    const trimmed = serviceInput.trim();
    if (!trimmed) return;
    if (formData.services.includes(trimmed)) {
      showError("This service is already added");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      services: [...prev.services, trimmed],
    }));
    setServiceInput("");
  };

  const handleServiceInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddService();
    }
  };

  const removeService = (index) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index),
    }));
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, banner: file });
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.category.trim() || !formData.price) {
      showError("Name, Category and Price are required");
      return;
    }

    if (formData.services.length === 0) {
      showError("At least one service is required for the package");
      return;
    }

    try {
      setUploading(true);

      let bannerUrl = isEditMode ? packageData?.banner || null : null;

      if (formData.banner) {
        const { uploadPromise } = uploadFileToCloudinary(
          formData.banner,
          setProgress
        );
        const result = await uploadPromise;
        bannerUrl = result.url;
      }

      const payload = {
        name: formData.name.trim(),
        category: formData.category.trim(),
        description: formData.description.trim(),
        price: Number(formData.price),
        discountedPrice: formData.discountedPrice
          ? Number(formData.discountedPrice)
          : undefined,
        banner: bannerUrl,
        isActive: formData.isActive,
        services: formData.services,
      };

      if (isEditMode) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/packages/${packageData._id}`,
          payload
        );
        showSuccess("Package updated successfully");
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/packages`, payload);
        showSuccess("Package created successfully");
      }

      onSuccess();
      onClose();
    } catch (err) {
      showError(
        err?.response?.data?.message ||
        (isEditMode ? "Failed to update" : "Failed to create") + " package"
      );
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  const title = isEditMode ? "Edit Package" : "Create New Package";
  const submitText = isEditMode ? "Save Changes" : "Create Package";
  const submitColor = isEditMode
    ? "bg-primaryColor hover:bg-primaryBtnHoverColor"
    : "bg-orange-500 hover:bg-orange-600";

  return (
    <div
      onClick={handleClose}
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-3xl rounded-xl shadow-2xl max-h-[92vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-gradient-to-r from-primaryColor via-brandAccentColor to-primaryBtnHoverColor z-10">
          <h3 className="text-2xl text-white font-bold flex items-center gap-3">
            <span className="p-2 rounded-full bg-brandAccentColor text-primaryColor">{isEditMode ? (
              <Pencil />
            ) : (
              <Plus />
            )}</span>
            {title}
          </h3>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name & Category */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Package Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g. Bridal Glow Package"
                className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-orange-400 outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                placeholder="e.g. Special Packages, Summer Packages"
                className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-orange-400 outline-none"
                required
              />
            </div>
          </div>

          {/* Prices */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Original Price (₹) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) =>
                  setFormData({ ...formData, price: e.target.value })
                }
                placeholder="1500"
                min="0"
                className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-orange-400 outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discounted Price (₹)
              </label>
              <input
                type="number"
                value={formData.discountedPrice}
                onChange={(e) =>
                  setFormData({ ...formData, discountedPrice: e.target.value })
                }
                placeholder="1200"
                min="0"
                className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-orange-400 outline-none"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              rows={4}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Describe what this package includes..."
              className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-orange-400 outline-none resize-none"
            />
          </div>

          {/* Banner */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Banner Image
            </label>
            <label
              className={`relative flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 text-center transition cursor-pointer hover:border-orange-400 ${uploading ? "opacity-70 cursor-not-allowed" : ""
                } ${preview ? "border-solid border-gray-300" : "border-gray-400"}`}
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleImage}
                disabled={uploading}
                className="hidden"
              />

              {!preview && !uploading && (
                <>
                  <ImagePlus className="w-10 h-10 text-orange-500 mb-3" />
                  <p className="text-base font-medium">Click to upload banner</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Recommended: 1200×600
                  </p>
                </>
              )}

              {preview && !uploading && (
                <img
                  src={preview}
                  alt="Banner preview"
                  className="w-full max-h-64 object-cover rounded-lg shadow-sm"
                />
              )}

              {uploading && (
                <div className="w-full max-w-xs">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Uploading...</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-orange-500 h-full rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-center text-sm mt-2">
                    {progress.toFixed(0)}%
                  </p>
                </div>
              )}
            </label>
          </div>

          {/* Services Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Included Services <span className="text-red-500">*</span>{" "}
              <span className="text-gray-400 font-normal">
                ({formData.services.length} added)
              </span>
            </label>

            {/* Input row */}
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={serviceInput}
                onChange={(e) => setServiceInput(e.target.value)}
                onKeyDown={handleServiceInputKeyDown}
                placeholder="e.g. Hair Spa, Facial, Manicure..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-orange-400"
              />
              <button
                type="button"
                onClick={handleAddService}
                className="px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition flex items-center gap-1 whitespace-nowrap"
              >
                <Plus size={18} />
                Add
              </button>
            </div>

            <p className="text-xs text-gray-400 mb-3">
              Type a service name and click Add or press Enter.
            </p>

            {/* Added Services List */}
            {formData.services.length > 0 ? (
              <div className="space-y-2">
                {formData.services.map((serviceName, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-green-50 border border-green-100 px-4 py-2.5 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-green-200 text-green-800 text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {index + 1}
                      </span>
                      <p className="font-medium text-gray-800">{serviceName}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeService(index)}
                      className="text-red-400 hover:text-red-600 p-1 transition"
                    >
                      <Trash2 size={17} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 text-sm">
                No services added yet. Type above and click Add.
              </div>
            )}
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) =>
                setFormData({ ...formData, isActive: e.target.checked })
              }
              className="w-5 h-5 text-orange-500 border-gray-300 rounded focus:ring-orange-400"
            />
            <label htmlFor="isActive" className="text-gray-700 font-medium">
              Package is active and visible to customers
            </label>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-8 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className={`px-8 py-3 text-white rounded-lg font-medium transition disabled:opacity-60 flex items-center gap-2 ${submitColor}`}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {isEditMode ? "Saving..." : "Creating..."}
                </>
              ) : (
                submitText
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PackageFormModal;









// // src/components/AdminSideComponents/packages/PackageFormModal.jsx
// import React, { useState, useEffect } from "react";
// import {
//   X,
//   ImagePlus,
//   Loader2,
//   Plus,
//   Minus,
//   Search,
//   Trash2,
//   Pencil,
// } from "lucide-react";
// import uploadFileToCloudinary from "../../../utils/uploadFileToCloudinary";
// import axios from "axios";
// import { showError, showSuccess } from "../../../utils/toastUtils";

// const PackageFormModal = ({ isOpen, onClose, packageData = null, onSuccess }) => {
//   const isEditMode = !!packageData;

//   const [formData, setFormData] = useState({
//     name: "",
//     category: "",
//     description: "",
//     price: "",
//     discountedPrice: "",
//     banner: null,
//     isActive: true,
//     services: [], // array of { serviceId, name, price }
//   });

//   const [preview, setPreview] = useState(null);
//   const [uploading, setUploading] = useState(false);
//   const [progress, setProgress] = useState(0);

//   // Services list from backend
//   const [allServices, setAllServices] = useState([]);
//   const [search, setSearch] = useState("");
//   const [loadingServices, setLoadingServices] = useState(false);

//   useEffect(() => {
//     if (isOpen) {
//       fetchAllServices();

//       if (isEditMode && packageData) {
//         setFormData({
//           name: packageData.name || "",
//           category: packageData.category || "",
//           description: packageData.description || "",
//           price: packageData.price || "",
//           discountedPrice: packageData.discountedPrice || "",
//           banner: null, // new file only
//           isActive: packageData.isActive ?? true,
//           services: (packageData.services || []).map((s) => ({
//             serviceId: s._id || s.serviceId,
//             name: s.name || "Unnamed Service",
//             price: s.price || 0,
//           })),
//         });
//         setPreview(packageData.banner || null);
//       } else {
//         // Reset for create mode
//         setFormData({
//           name: "",
//           category: "",
//           description: "",
//           price: "",
//           discountedPrice: "",
//           banner: null,
//           isActive: true,
//           services: [],
//         });
//         setPreview(null);
//       }
//     }
//   }, [isOpen, packageData, isEditMode]);

//   const fetchAllServices = async () => {
//     setLoadingServices(true);
//     try {
//       const res = await axios.get(
//         `${process.env.REACT_APP_API_URL}/services/get-all`
//       );
//       setAllServices(res.data.data || []);
//     } catch (err) {
//       showError("Failed to load services list");
//     } finally {
//       setLoadingServices(false);
//     }
//   };

//   const filteredServices = allServices.filter((s) =>
//     s.name.toLowerCase().includes(search.toLowerCase())
//   );

//   const addService = (service) => {
//     const alreadyAdded = formData.services.some(
//       (s) => s.serviceId === service._id
//     );
//     if (alreadyAdded) return;

//     setFormData((prev) => ({
//       ...prev,
//       services: [
//         ...prev.services,
//         {
//           serviceId: service._id,
//           name: service.name,
//           price: service.discountedPrice || service.price || 0,
//         },
//       ],
//     }));
//   };

//   const removeService = (serviceId) => {
//     setFormData((prev) => ({
//       ...prev,
//       services: prev.services.filter((s) => s.serviceId !== serviceId),
//     }));
//   };

//   const handleImage = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       setFormData({ ...formData, banner: file });
//       setPreview(URL.createObjectURL(file));
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!formData.name.trim() || !formData.category.trim() || !formData.price) {
//       showError("Name, Category and Price are required");
//       return;
//     }

//     if (formData.services.length === 0) {
//       showError("At least one service is required for the package");
//       return;
//     }

//     try {
//       setUploading(true);

//       let bannerUrl = isEditMode ? packageData?.banner || null : null;

//       if (formData.banner) {
//         const { uploadPromise } = uploadFileToCloudinary(
//           formData.banner,
//           setProgress
//         );
//         const result = await uploadPromise;
//         bannerUrl = result.url;
//       }

//       const payload = {
//         name: formData.name.trim(),
//         category: formData.category.trim(),
//         description: formData.description.trim(),
//         price: Number(formData.price),
//         discountedPrice: formData.discountedPrice
//           ? Number(formData.discountedPrice)
//           : undefined,
//         banner: bannerUrl,
//         isActive: formData.isActive,
//         services: formData.services.map((s) => s.serviceId),
//       };

//       if (isEditMode) {
//         await axios.put(
//           `${process.env.REACT_APP_API_URL}/packages/${packageData._id}`,
//           payload
//         );
//         showSuccess("Package updated successfully");
//       } else {
//         await axios.post(`${process.env.REACT_APP_API_URL}/packages`, payload);
//         showSuccess("Package created successfully");
//       }

//       onSuccess();
//       onClose();
//     } catch (err) {
//       showError(
//         err?.response?.data?.message ||
//         (isEditMode ? "Failed to update" : "Failed to create") + " package"
//       );
//     } finally {
//       setUploading(false);
//       setProgress(0);
//     }
//   };

//   const handleClose = () => {
//     onClose();
//   };

//   if (!isOpen) return null;

//   const title = isEditMode ? "Edit Package" : "Create New Package";
//   const submitText = isEditMode ? "Save Changes" : "Create Package";
//   const submitColor = isEditMode ? "bg-primaryColor hover:bg-primaryBtnHoverColor" : "bg-orange-500 hover:bg-orange-600";

//   return (
//     <div
//       onClick={handleClose}
//       className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
//     >
//       <div
//         onClick={(e) => e.stopPropagation()}
//         className="bg-white w-full max-w-3xl rounded-xl shadow-2xl max-h-[92vh] overflow-y-auto"
//       >
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
//           <h3 className="text-2xl font-bold flex items-center gap-3">
//             {isEditMode ? (
//               <Pencil className="text-primaryColor" />
//             ) : (
//               <Plus className="text-primaryColor" />
//             )}
//             {title}
//           </h3>
//           <button
//             onClick={handleClose}
//             className="p-2 hover:bg-gray-100 rounded-full transition"
//           >
//             <X className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Form */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Name & Category */}
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Package Name <span className="text-red-500">*</span>
//               </label>
//               <input
//                 type="text"
//                 value={formData.name}
//                 onChange={(e) => setFormData({ ...formData, name: e.target.value })}
//                 placeholder="e.g. Bridal Glow Package"
//                 className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-orange-400 outline-none"
//                 required
//               />
//             </div>
           

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Category <span className="text-red-500">*</span>
//               </label>
//               <input
//                 type="text"
//                 value={formData.category}
//                 onChange={(e) => setFormData({ ...formData, category: e.target.value })}
//                 placeholder="e.g. Special Packages, Summer Packages"
//                 className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-orange-400 outline-none"
//                 required
//               />
//             </div>
//           </div>

//           {/* Prices */}
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Original Price (₹) <span className="text-red-500">*</span>
//               </label>
//               <input
//                 type="number"
//                 value={formData.price}
//                 onChange={(e) => setFormData({ ...formData, price: e.target.value })}
//                 placeholder="1500"
//                 min="0"
//                 className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-orange-400 outline-none"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Discounted Price (₹)
//               </label>
//               <input
//                 type="number"
//                 value={formData.discountedPrice}
//                 onChange={(e) => setFormData({ ...formData, discountedPrice: e.target.value })}
//                 placeholder="1200"
//                 min="0"
//                 className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-orange-400 outline-none"
//               />
//             </div>
//           </div>

//           {/* Description */}
//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Description
//             </label>
//             <textarea
//               rows={4}
//               value={formData.description}
//               onChange={(e) => setFormData({ ...formData, description: e.target.value })}
//               placeholder="Describe what this package includes..."
//               className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-orange-400 outline-none resize-none"
//             />
//           </div>

//           {/* Banner */}
//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-2">
//               Banner Image
//             </label>
//             <label
//               className={`relative flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 text-center transition cursor-pointer hover:border-orange-400 ${uploading ? "opacity-70 cursor-not-allowed" : ""
//                 } ${preview ? "border-solid border-gray-300" : "border-gray-400"}`}
//             >
//               <input
//                 type="file"
//                 accept="image/*"
//                 onChange={handleImage}
//                 disabled={uploading}
//                 className="hidden"
//               />

//               {!preview && !uploading && (
//                 <>
//                   <ImagePlus className="w-10 h-10 text-orange-500 mb-3" />
//                   <p className="text-base font-medium">Click to upload banner</p>
//                   <p className="text-sm text-gray-500 mt-1">Recommended: 1200×600</p>
//                 </>
//               )}

//               {preview && !uploading && (
//                 <img
//                   src={preview}
//                   alt="Banner preview"
//                   className="w-full max-h-64 object-cover rounded-lg shadow-sm"
//                 />
//               )}

//               {uploading && (
//                 <div className="w-full max-w-xs">
//                   <div className="flex items-center justify-center gap-3 mb-3">
//                     <Loader2 className="w-5 h-5 animate-spin" />
//                     <span>Uploading...</span>
//                   </div>
//                   <div className="w-full bg-gray-200 rounded-full h-2.5">
//                     <div
//                       className="bg-orange-500 h-full rounded-full transition-all"
//                       style={{ width: `${progress}%` }}
//                     />
//                   </div>
//                   <p className="text-center text-sm mt-2">{progress.toFixed(0)}%</p>
//                 </div>
//               )}
//             </label>
//           </div>

//           {/* Services Selection */}
//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-2">
//               Included Services <span className="text-red-500">*</span> ({formData.services.length})
//             </label>

//             {/* Search */}
//             <div className="relative mb-3">
//               <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
//               <input
//                 type="text"
//                 placeholder="Search services..."
//                 value={search}
//                 onChange={(e) => setSearch(e.target.value)}
//                 className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-orange-400"
//               />
//             </div>

//             {/* Selected Services */}
//             {formData.services.length > 0 && (
//               <div className="mb-4 space-y-2">
//                 {formData.services.map((s) => (
//                   <div
//                     key={s.serviceId}
//                     className="flex items-center justify-between bg-green-50 border border-green-100 px-4 py-2.5 rounded-lg"
//                   >
//                     <div>
//                       <p className="font-medium text-gray-800">{s.name}</p>
//                       {/* <p className="text-xs text-gray-600">₹{s.price}</p> */}
//                     </div>
//                     <button
//                       type="button"
//                       onClick={() => removeService(s.serviceId)}
//                       className="text-red-500 hover:text-red-700 p-1"
//                     >
//                       <Trash2 size={18} />
//                     </button>
//                   </div>
//                 ))}
//               </div>
//             )}

//             {/* Services List */}
//             <div className="border rounded-xl overflow-hidden bg-gray-50 max-h-60 overflow-y-auto">
//               {loadingServices ? (
//                 <div className="flex justify-center py-8">
//                   <Loader2 className="animate-spin text-orange-500" size={24} />
//                 </div>
//               ) : filteredServices.length === 0 ? (
//                 <p className="text-center py-8 text-gray-500">No services found</p>
//               ) : (
//                 filteredServices.map((service) => {
//                   const isSelected = formData.services.some(
//                     (s) => s.serviceId === service._id
//                   );
//                   return (
//                     <div
//                       key={service._id}
//                       onClick={() => !isSelected && addService(service)}
//                       className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-white transition ${isSelected ? "bg-green-50" : ""
//                         }`}
//                     >
//                       <div>
//                         <p className="font-medium text-gray-800">{service.name}</p>
//                         {/* <p className="text-sm text-gray-600">
//                           ₹{service.discountedPrice || service.price || "—"}
//                         </p> */}
//                       </div>
//                       {isSelected ? (
//                         <span className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">
//                           Added
//                         </span>
//                       ) : (
//                         <Plus className="text-gray-400" size={20} />
//                       )}
//                     </div>
//                   );
//                 })
//               )}
//             </div>
//           </div>

//           {/* Active Toggle */}
//           <div className="flex items-center gap-3">
//             <input
//               type="checkbox"
//               id="isActive"
//               checked={formData.isActive}
//               onChange={(e) =>
//                 setFormData({ ...formData, isActive: e.target.checked })
//               }
//               className="w-5 h-5 text-orange-500 border-gray-300 rounded focus:ring-orange-400"
//             />
//             <label htmlFor="isActive" className="text-gray-700 font-medium">
//               Package is active and visible to customers
//             </label>
//           </div>

//           {/* Footer Buttons */}
//           <div className="flex justify-end gap-4 pt-6 border-t">
//             <button
//               type="button"
//               onClick={handleClose}
//               className="px-8 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={uploading}
//               className={`px-8 py-3 text-white rounded-lg font-medium transition disabled:opacity-60 flex items-center gap-2 ${submitColor}`}
//             >
//               {uploading ? (
//                 <>
//                   <Loader2 className="w-5 h-5 animate-spin" />
//                   {isEditMode ? "Saving..." : "Creating..."}
//                 </>
//               ) : (
//                 submitText
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default PackageFormModal;